import { ControleToiListItem } from '../models/controle-toi.model';
import {
  MxlAtividadeToiLoteIndicadores,
  MxlAtividadeToiLoteItemOutput,
  MxlAtividadeToiLoteItemsOutput,
  MxlAtividadeToiLoteOutput,
  TratamentoLote,
  TratamentoLoteFiltroRapido,
  TratamentoLoteFiltroRapidoOption,
  TratamentoLoteItem,
  TratamentoLoteOrdenacaoDataEnvio,
  TratamentoLoteResumo,
  TratamentoLoteTagSeverity,
} from '../models/tratamento-lote.model';

/** Tamanho máximo do lote para envio — validado pelo backend. */
export const TAMANHO_MAXIMO_LOTE_MB = 200;

export const TRATAMENTO_LOTE_ORDENACAO_DATA_ENVIO_OPTIONS: {
  label: string;
  value: TratamentoLoteOrdenacaoDataEnvio;
}[] = [
  { label: 'Data envio (mais recente)', value: 'desc' },
  { label: 'Data envio (mais antigo)', value: 'asc' },
];

export const TRATAMENTO_LOTE_FILTRO_OPTIONS: TratamentoLoteFiltroRapidoOption[] = [
  { label: 'Todos', value: 'todos' },
  { label: 'Com erro', value: 'com_erro' },
  { label: 'Pendentes', value: 'pendentes' },
  { label: 'Aguardando confirmação', value: 'aguardando_confirmacao' },
];

/** Lote aguardando confirmação (OK) dos Correios para envio definitivo. */
export function loteProntoParaEnvio(lote: TratamentoLote): boolean {
  return lote.statusLote === 'Aguardando confirmação';
}

export function loteEmFormacao(lote: TratamentoLote): boolean {
  return lote.statusLote === 'Em formação' || lote.statusLote === 'Lote Completo';
}

/** Lote ainda não enviado marcado como completo pelo backend (LOTE_COMPLETO). */
export function loteCompleto(lote: TratamentoLote): boolean {
  return lote.statusLote === 'Lote Completo';
}

/** Lote que retornou dos Correios com erro de endereço e aguarda correção. */
export function loteTratamentoPendente(lote: TratamentoLote): boolean {
  return lote.statusLote === 'Cancelado - Tratamento Pendente';
}

/** Lote fechado por correção de endereço — as OS seguem em um novo lote (reenvio). */
export function loteCancelado(lote: TratamentoLote): boolean {
  return (
    lote.statusLote === 'Cancelado - Tratamento Pendente' ||
    lote.statusLote === 'Cancelado - Tratamento Realizado'
  );
}

/** Após o reenvio (tratamento realizado) as OS já estão em outro lote — não cabe mais ajuste de endereço. */
export function lotePermiteCorrecaoEndereco(lote: TratamentoLote): boolean {
  return lote.statusLote !== 'Cancelado - Tratamento Realizado';
}

/** Lote criado a partir de um lote cancelado (possui lote original). */
export function loteEhReenvio(lote: TratamentoLote): boolean {
  return !!lote.idLoteOriginal;
}

/** Lote já enviado anteriormente ou originado de um lote cancelado — envio vira "Reenviar". */
export function loteExibeReenvio(lote: TratamentoLote): boolean {
  return !!lote.dataEnvio || loteEhReenvio(lote);
}

/**
 * Envio só é permitido antes de o lote seguir para os Correios (em formação ou completo).
 * A partir de "Aguardando Correios" o botão Enviar deixa de ser exibido.
 */
export function lotePermiteEnvioManual(lote: TratamentoLote): boolean {
  return loteEmFormacao(lote) && !loteCancelado(lote);
}

/** Retorno dos Correios leva ~50 min; a confirmação só é liberada 1h após o envio. */
export const TEMPO_MINIMO_CONFIRMACAO_LOTE_MS = 60 * 60 * 1000;

export function lotePermiteConfirmacao(lote: TratamentoLote, agora = Date.now()): boolean {
  if (loteCancelado(lote) || !loteProntoParaEnvio(lote) || !lote.dataEnvio) {
    return false;
  }
  const envio = new Date(lote.dataEnvio).getTime();
  return !Number.isNaN(envio) && agora - envio >= TEMPO_MINIMO_CONFIRMACAO_LOTE_MS;
}

export function loteTemErroEndereco(lote: TratamentoLote): boolean {
  return (
    loteTratamentoPendente(lote) || lote.itens.some((item) => item.flagErroEndereco)
  );
}

export function loteStatusSeverity(status: string): TratamentoLoteTagSeverity {
  switch (status) {
    case 'Enviado':
    case 'Lote Completo':
      return 'success';
    case 'Aguardando confirmação':
      return 'info';
    case 'Aguardando Correios':
      return 'contrast';
    case 'Em formação':
      return 'warn';
    case 'Cancelado - Tratamento Pendente':
    case 'Erro de endereço':
      return 'danger';
    case 'No lote':
    case 'Cancelado - Tratamento Realizado':
      return 'secondary';
    default:
      return 'secondary';
  }
}

export function calcularResumoLotes(lotes: TratamentoLote[]): TratamentoLoteResumo {
  return lotes.reduce(
    (resumo, lote) => {
      if (loteTemErroEndereco(lote)) resumo.totalErros++;
      if (loteEmFormacao(lote)) resumo.totalPendentes++;
      if (lote.statusLote === 'Enviado') resumo.totalEnviados++;
      if (loteCancelado(lote)) resumo.totalCancelados++;
      return resumo;
    },
    {
      totalLotes: lotes.length,
      totalErros: 0,
      totalPendentes: 0,
      totalEnviados: 0,
      totalCancelados: 0,
    }
  );
}

/** Monta o resumo dos cards a partir dos indicadores agregados do backend. */
export function resumoFromIndicadores(
  indicadores: MxlAtividadeToiLoteIndicadores | null | undefined,
  totalRegistros: number
): TratamentoLoteResumo {
  return {
    totalLotes: totalRegistros,
    totalErros: indicadores?.erros ?? 0,
    totalPendentes: indicadores?.lotesEmFormacao ?? 0,
    totalEnviados: indicadores?.enviados ?? 0,
    // Backend ainda não retorna indicador de cancelados.
    totalCancelados: 0,
  };
}

export function filtrarLotes(
  lotes: TratamentoLote[],
  filtro: TratamentoLoteFiltroRapido
): TratamentoLote[] {
  switch (filtro) {
    case 'com_erro':
      return lotes.filter((lote) => loteTemErroEndereco(lote));
    case 'pendentes':
      return lotes.filter((lote) => loteEmFormacao(lote));
    case 'aguardando_confirmacao':
      return lotes.filter((lote) => lote.statusLote === 'Aguardando confirmação');
    case 'enviados':
      return lotes.filter((lote) => lote.statusLote === 'Enviado');
    case 'cancelados':
      return lotes.filter((lote) => loteCancelado(lote));
    default:
      return lotes;
  }
}

export function ordenarLotesPorDataEnvio(
  lotes: TratamentoLote[],
  ordem: TratamentoLoteOrdenacaoDataEnvio
): TratamentoLote[] {
  return [...lotes].sort((a, b) => {
    const dataA = a.dataEnvio ? new Date(a.dataEnvio).getTime() : null;
    const dataB = b.dataEnvio ? new Date(b.dataEnvio).getTime() : null;

    if (dataA === null && dataB === null) {
      return 0;
    }
    if (dataA === null) {
      return 1;
    }
    if (dataB === null) {
      return -1;
    }

    return ordem === 'asc' ? dataA - dataB : dataB - dataA;
  });
}

export function localizarLotePorContexto(
  lotes: TratamentoLote[],
  itemContexto: ControleToiListItem | null | undefined
): TratamentoLote | undefined {
  if (!itemContexto) {
    return undefined;
  }

  const itemRecord = itemContexto as ControleToiListItem & { nrLoteCorreios?: string | null };
  const nrLote = itemRecord.nrLoteCorreios?.trim();

  if (nrLote) {
    const porNumero = lotes.find((lote) => lote.nrLoteCorreios === nrLote);
    if (porNumero) {
      return porNumero;
    }
  }

  return lotes.find((lote) =>
    lote.itens.some((item) => item.idAtividade === itemContexto.idAtividade)
  );
}

export function truncarTexto(texto: string | null | undefined, max = 60): string {
  const valor = texto?.trim();
  if (!valor) {
    return '-';
  }
  return valor.length > max ? `${valor.slice(0, max)}…` : valor;
}

/** Lote já enviado ou em fluxo com os Correios (exibe andamento no detalhe). */
export function loteExibeAndamentoCorreios(lote: TratamentoLote): boolean {
  return (
    lote.statusLote === 'Enviado' ||
    lote.statusLote === 'Aguardando Correios' ||
    lote.statusLote === 'Aguardando confirmação' ||
    loteTratamentoPendente(lote)
  );
}

/** Exibe a data de envio sempre que o backend a preencher, independente do status. */
export function loteExibeDataEnvio(lote: TratamentoLote): boolean {
  return !!lote.dataEnvio;
}

/** Converte o output da API (MxlAtividadeToiLoteOutput) no modelo de apresentação. */
export function mapLoteOutput(output: MxlAtividadeToiLoteOutput): TratamentoLote {
  const status = output.nmStatus ?? '';
  const isErro = status === 'Cancelado - Tratamento Pendente';
  const retorno = output.retorno ?? null;

  return {
    idLote: output.idAtividadeToiLote,
    idAtividadeToiLote: output.idAtividadeToiLote,
    nrLoteCorreios: output.nrLote ?? '',
    idLoteOriginal: output.idAtividadeLoteOriginal ?? null,
    nrLoteOriginal: output.nrLoteOriginal ?? null,
    dataEnvio: output.dataEnvio ?? null,
    dataProcessamento: null,
    statusLote: status,
    dsRetornoErro: isErro ? retorno : null,
    dsRetornoCorreios: isErro ? null : retorno,
    qtdItens: output.quantidadeItens ?? 0,
    itens: [],
    itensCarregados: false,
  };
}

/** Converte o output da API (MxlAtividadeToiLoteItemOutput) no item de apresentação. */
export function mapLoteItemOutput(output: MxlAtividadeToiLoteItemOutput): TratamentoLoteItem {
  const pendencia = output.pendencia?.trim() || null;
  const flagErroEndereco = !!output.flagErro;

  return {
    idAtividadeToiLoteItem: output.idAtividadeToiLoteItem,
    idAtividadeToi: output.idAtividadeToi,
    numeroBa: output.os ?? '',
    nrDocumento: output.documento ?? '',
    dsEnderecoUnidade: output.endereco ?? '',
    statusItem: output.nmStatus ?? '',
    flagErroEndereco,
    flagAguardandoCorrecaoEndereco: !flagErroEndereco && !!pendencia,
    dsRetornoErro: pendencia,
  };
}

/** GET /GetListaLoteItems pode responder um array puro ou um envelope com nomes variados. */
export function mapLoteItemsResponse(
  response: MxlAtividadeToiLoteItemsOutput | MxlAtividadeToiLoteItemOutput[] | null | undefined
): TratamentoLoteItem[] {
  if (Array.isArray(response)) {
    return response.map(mapLoteItemOutput);
  }

  const record = (response ?? {}) as MxlAtividadeToiLoteItemsOutput & Record<string, unknown>;
  const rawItens =
    record.itens ?? record['Itens'] ?? record['items'] ?? record['data'] ?? record['dados'];

  if (!Array.isArray(rawItens)) {
    return [];
  }

  return rawItens.map((item) => mapLoteItemOutput(item as MxlAtividadeToiLoteItemOutput));
}
